import { Field, Label, Switch } from "@headlessui/react";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import Error from "components/Error";
import Button from "components/buttons/Button";
import { Calendar } from "components/ui/calendar";
import {
  BusyPeriodFormValues,
  OutOfOfficeFormMode,
  SupplierBusyPeriod,
} from "pages/out-of-office/out-of-office.typing";
import { useOutOfOfficeFormView } from "pages/out-of-office/useOutOfOfficeFormView";
import { useTranslation } from "react-i18next";
import { cn } from "utils/ui";

interface Props {
  mode: OutOfOfficeFormMode;
  initialPeriod?: SupplierBusyPeriod;
  apiError?: string | null;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: BusyPeriodFormValues) => void;
}

const OutOfOfficeFormView = ({ mode, initialPeriod, apiError, isSubmitting = false, onCancel, onSubmit }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "outOfOffice.form" });
  const {
    earliestMonth,
    disabledDays,
    displayedMonth,
    setDisplayedMonth,
    values,
    validationError,
    overlapWarningDays,
    isCheckingOverlap,
    dateRange,
    commentMaxLength,
    handleDateSelect,
    handleCanAcceptSmallAssignmentsChange,
    handleCommentChange,
    handleSubmit,
  } = useOutOfOfficeFormView(initialPeriod, onSubmit);

  const errorToShow = validationError ?? apiError ?? null;

  return (
    <div className="flex flex-col" data-test="out-of-office-form-view">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-slate-200 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          onClick={onCancel}
          title={t("back")}
          data-test="button-out-of-office-back"
          disabled={isSubmitting}
        >
          <span className="sr-only">{t("back")}</span>
          <ChevronLeftIcon className="size-6" aria-hidden="true" />
        </button>
        <h3 className="text-color-primary text-lg font-medium leading-6 sm:text-2xl">
          {mode === "create" ? t("createTitle") : t("editTitle")}
        </h3>
      </div>

      {errorToShow ? (
        <div className="mt-4">
          <Error error={errorToShow} />
        </div>
      ) : null}

      <form className="mt-6 flex flex-col space-y-6" onSubmit={handleSubmit}>
        <div>
          <div className="mt-2 flex justify-center">
            <Calendar
              autoFocus
              mode="range"
              startMonth={earliestMonth}
              month={displayedMonth}
              onMonthChange={setDisplayedMonth}
              selected={dateRange}
              onSelect={handleDateSelect}
              disabled={disabledDays}
              numberOfMonths={1}
              className="w-fit p-0"
            />
          </div>
          {isCheckingOverlap ? (
            <p className="text-color-secondary mt-2 text-center text-sm" data-test="busy-period-overlap-checking">
              {t("overlapChecking")}
            </p>
          ) : null}
          {overlapWarningDays ? (
            <p
              className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-center text-sm text-amber-800 dark:bg-amber-900/40 dark:text-amber-100"
              data-test="busy-period-overlap-warning"
            >
              {t("overlapWarning", { days: overlapWarningDays })}
            </p>
          ) : null}
        </div>

        <Field as="div" className="flex items-start justify-between gap-4">
          <Label as="div" className="min-w-0 flex-1 cursor-pointer">
            <span className="text-color-primary block text-sm font-medium">{t("canAcceptSmallAssignments")}</span>
            <span className="text-color-secondary mt-0.5 block text-sm">{t("canAcceptSmallAssignmentsHint")}</span>
          </Label>
          <Switch
            checked={values.canAcceptSmallAssignments}
            onChange={handleCanAcceptSmallAssignmentsChange}
            className={cn(
              values.canAcceptSmallAssignments ? "bg-ribbon-500" : "bg-gray-200 dark:bg-gray-700",
              "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-ribbon-400"
            )}
            data-test="switch-can-accept-small-assignments"
          >
            <span
              aria-hidden="true"
              className={cn(
                values.canAcceptSmallAssignments ? "translate-x-5" : "translate-x-0",
                "pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              )}
            />
          </Switch>
        </Field>

        <div>
          <label htmlFor="busy-period-comment" className="text-color-primary block text-sm font-medium">
            {t("comment")}
          </label>
          <div className="mt-2">
            <textarea
              id="busy-period-comment"
              rows={3}
              maxLength={commentMaxLength}
              placeholder={t("commentPlaceholder")}
              value={values.comments}
              onChange={handleCommentChange}
              data-test="busy-period-comment-input"
              className="block w-full rounded-md border-gray-300 shadow-sm placeholder:text-slate-400 focus:border-ribbon-400 focus:outline-none focus:ring-1 focus:ring-ribbon-400 dark:border-slate-700 dark:bg-slate-600 dark:text-slate-200 sm:text-sm"
            />
            <p className="text-color-secondary mt-1 text-xs leading-4">
              {t("commentMaxLengthHint", { max: commentMaxLength })}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            color="ternary"
            label={t("cancel")}
            onClick={onCancel}
            disabled={isSubmitting}
            dataTestId="button-ooo-cancel"
          />
          <Button
            type="submit"
            color="primary"
            label={mode === "create" ? t("create") : t("save")}
            disabled={isSubmitting}
            dataTestId="button-ooo-submit"
          />
        </div>
      </form>
    </div>
  );
};

export default OutOfOfficeFormView;
